import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { settings as settingsApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { User, Shield, Loader2 } from 'lucide-react';

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({ full_name: '', company_name: '' });
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '' });
  const [loading, setLoading] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);

  useEffect(() => {
    settingsApi.get().then(r => {
      setForm({ full_name: r.data.full_name || '', company_name: r.data.company_name || '' });
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setLoading(true);
    try {
      await settingsApi.update(form);
      await refreshUser();
      toast.success('Settings updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
    setLoading(false);
  };

  const handlePasswordChange = async () => {
    if (pwForm.new_password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setPwLoading(true);
    try {
      await settingsApi.update(pwForm);
      setPwForm({ current_password: '', new_password: '' });
      toast.success('Password changed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Password change failed');
    }
    setPwLoading(false);
  };

  return (
    <div data-testid="settings-page" className="space-y-6 max-w-2xl">
      <div>
        <h2 className="text-3xl font-bold tracking-tight font-['Plus_Jakarta_Sans']">Settings</h2>
        <p className="text-muted-foreground mt-1">Manage your account</p>
      </div>

      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="w-4 h-4 text-primary" />
            Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Email</Label>
            <Input value={user?.email || ''} disabled className="opacity-60" />
          </div>
          <div className="space-y-2">
            <Label>Full Name</Label>
            <Input data-testid="settings-name" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />
          </div>
          <div className="space-y-2">
            <Label>Company</Label>
            <Input data-testid="settings-company" value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} />
          </div>
          <Button data-testid="save-settings" onClick={handleSave} disabled={loading} className="bg-primary hover:bg-primary/90">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary" />
            Change Password
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Current Password</Label>
            <Input data-testid="current-password" type="password" value={pwForm.current_password} onChange={e => setPwForm(f => ({ ...f, current_password: e.target.value }))} />
          </div>
          <div className="space-y-2">
            <Label>New Password</Label>
            <Input data-testid="new-password" type="password" value={pwForm.new_password} onChange={e => setPwForm(f => ({ ...f, new_password: e.target.value }))} />
          </div>
          <Button data-testid="change-password" onClick={handlePasswordChange} disabled={pwLoading} variant="outline">
            {pwLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Change Password'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
